import { TiVoDeviceOem } from "hooks/tivoDevices";
import { IMAGES } from "pages/TiVoApps/constants";

import {
  APP_CATEGORIES,
  APP_TYPE,
  COMMERCIAL_AGREEMENT_TYPE,
  CONTRACT_ORIGIN,
  CONTRACT_STATUS,
  PARTNER_PICKS_REPORTINGS,
  PARTNER_PICKS_TYPES,
  RECOMMENDATION_TYPES,
  ReleaseStage,
  Status,
  SUPPORT_PRIORITY,
  URL_PARAMETER_PLACEMENT,
  YES_NO_UNKNOWN,
} from "./constants";

export type TiVoAcrRights = "no" | "notMentioned" | "yes";

export type TiVoTargets = {
  platforms?: string[];
  oems?: string[];
  clientTypes?: string[];
};

export type TiVoCspBizModel =
  | "avod"
  | "fast"
  | "svod"
  | "tvod"
  | "unmonetized"
  | "vmvpd";

export type TiVoCspGroup =
  | "global_t1"
  | "global_t2"
  | "local_t1"
  | "local_t2"
  | "long_tail"
  | "other_premium";

export type TiVoImage = {
  environment: "dev" | "prod" | "staging";
  imageType:
    | "atmospheric"
    | "partnerSourceLogoSquare"
    | "partnerSourceLogoWide"
    | "primaryBrandingLogoRectangle"
    | "primaryBrandingLogoSquare";
  providerId: string;
};

export type TiVoLaunchAttributes = {
  adParamsTemplate?: string;
  appendUrlParameters?: {
    key: string;
    name: string;
    placement?: keyof typeof URL_PARAMETER_PLACEMENT | null;
    value?: string | null;
  }[];
  attributesWithCriteria?: string;
  intent?: string;
  playbackUri?: string;
};

export const TIVO_LAUNCH_TYPES = [
  "launch",
  "linear",
  "searchLaunch",
  "seriesBrowse",
  "vod",
] as const;

export type TiVoLaunchType = (typeof TIVO_LAUNCH_TYPES)[number];

export type TiVoAssets = Partial<
  Record<
    TiVoImage["environment"],
    Partial<
      Record<
        IMAGES,
        {
          url: string;
          width: number | null;
          height: number | null;
        }
      >
    >
  >
>;

export type TiVoPresentationAttributes = {
  alwaysVisibleInMyApps?: boolean | null;
  appManifestAvailable?: boolean | null;
  browsePlaybackSortOrder?: number | null;
  enabledByDefault?: boolean | null;
  inMyAppsByDefault?: boolean | null;
  myAppsRangeGroup?: number | null;
  name?: string | null;
  platformSupportedApps?: string[] | null;
  promoted?: boolean | null;
  recommendationsType?: keyof typeof RECOMMENDATION_TYPES | null;
  shouldShowInList?: boolean | null;
  shouldShowInSearchInApps?: boolean | null;
  sortOrder?: number | null;
  supportsEpisodeDeepLinks?: boolean | null;
  postPublishDaysConsideredNew?: number | null;
  publishDateTime?: string | null;
};

export type TiVoPresentationConfig = {
  attributes: TiVoPresentationAttributes;
  countries: string[];
  platformSupportedFeatures?: string[] | null;
};

export type TiVoUriTransform = {
  enabled: boolean;

  alternateBasePlaybackUrl?: string | null;
  className?: string | null;
  providerSpecificNameValueMap?: Record<string, string> | null;
};

export type TiVoPresentation = {
  configs: TiVoPresentationConfig[];
  label: string;
  partnerPicks: {
    aspectRatio?: string | null;
    reporting?: keyof typeof PARTNER_PICKS_REPORTINGS | null;
    type?: keyof typeof PARTNER_PICKS_TYPES | null;
  } | null;
  releaseStage: ReleaseStage;
  targets: TiVoTargets;
};

export type TiVoAppProvider = {
  id: string;
  allowList?: string[] | null;
  appCategories: (keyof typeof APP_CATEGORIES)[];
  assets: TiVoAssets;
  creationDate: number;
  countries: string[];
  displayName: string;
  label: string;
  providerId: string;
  universalProviderId: string;
  jwProviderId?: string[] | null;
  modificationDate: number;

  // contract
  acrRights?: TiVoAcrRights | null;
  assignedBdm?: string;
  assignedTam?: string;
  contractOrigin?: keyof typeof CONTRACT_ORIGIN | null;
  contractStatus?: keyof typeof CONTRACT_STATUS | null;
  commercialAgreementType?: (keyof typeof COMMERCIAL_AGREEMENT_TYPE)[] | null;
  commercialAgreementSummary?: string | null;
  cspBusinessModel?: TiVoCspBizModel[] | null;
  cspConfirmationRequired?: keyof typeof YES_NO_UNKNOWN | null;
  cspGroup?: TiVoCspGroup | null;
  dealSummary?: string | null;
  placementObligations?: string | null;
  promotionsObligations?: string | null;
  reportingObligations?: string | null;

  // support
  supportAdditionalContacts?: string | null;
  supportEmail?: string | null;
  supportNotes?: string | null;
  supportPhone?: string | null;
  supportPriority?: keyof typeof SUPPORT_PRIORITY | null;
  supportSla?: string | null;

  // Included when the include parameter contains "variantDraft".
  variantDraft?: TiVoAppVariantDraft;
  // Included when the include parameter contains "variants".
  variants?: TiVoAppVariant[];
  // Included when the include parameter contains "presentations".
  presentations?: TiVoPresentation[];
};

export type ZoomConfiguration = {
  targets: TiVoTargets;
  zoomFactor?: number | null;
  zoomFactorRatio?: number | null;
};

type TiVoAppVariantVersionBase = {
  id: string;
  adsEnabled: boolean;
  countries: TiVoAppProvider["countries"];
  label: string;
  launchAttributes: { appType: keyof typeof APP_TYPE } & {
    [K in TiVoLaunchType]?: TiVoLaunchAttributes;
  };
  targets: TiVoTargets;
  uriTransform: TiVoUriTransform;
  zoomConfigurations: ZoomConfiguration[];
};

export type TiVoAppVariantVersion = TiVoAppVariantVersionBase & {
  creationDate: number;
  releaseStages?: ReleaseStage[]; // draft version will not have release stages
};

export type TiVoAppVariantVersionInput = TiVoAppVariantVersionBase & {
  strict?: boolean; // True for backend to not allow duplicates
};

export type ApiServerErrorMetaMatchingVariantVersion = {
  matchingVariantVersions: {
    variantId: string;
    variantLabel: string;
    variantVersionId: string;
    variantVersionLabel: string;
  }[];
};

export type TiVoAppVariant = {
  id: string;
  creationDate?: number;
  // Populated from JSON API deserializer when using include parameter
  label: string;
  status: Status[];

  // Included when the include parameter contains "draftVersion".
  draftVersion?: TiVoAppVariantDraft;
  // Included when the include parameter contains "provider".
  provider?: TiVoAppProvider;
  // Included when the include parameter contains "versions".
  versions?: TiVoAppVariantVersion[];
};

export type PlatformPublication = {
  platformId: string;
  platformName?: string;
  publications: Record<string, Partial<Record<ReleaseStage, string[]>>>;
};

export type TiVoPublications = {
  id: string;
  platforms: PlatformPublication[];

  // Included when the include parameter contains "oems".
  oems?: TiVoDeviceOem[];
  // Included when the include parameter contains "variant".
  variant?: TiVoAppVariant;
};
// Raw data from BE
export type VariantPublications = Pick<
  TiVoPublications,
  "id" | "oems" | "variant"
> & {
  publications: PlatformPublication["publications"];
};

export type TiVoAppVariantDraft = {
  id: string;
  createdBy: string | null;
  creationDate: number;
  data: {
    attributes: Omit<
      TiVoAppVariantVersion,
      "id" | "creationDate" | "modificationDate"
    >;
  };
};

export type Filter = {
  id: string;
  description: string;
  displayName: string;
  param: string;
  hasSuggestions: boolean;
  values: { query: string; display: string }[];
};

export type FilterSuggestion = {
  text: string;
  filter_value: string;
  icon: string | null;
};

export type TiVoPublicationHistory = TiVoPublicationHistoryRow[];

export type TiVoPublicationHistoryRow = {
  id: string;
  modificationDate: string;
  modifiedBy?: {
    id: string;
    displayName: string;
  };
  entries: TiVoPublicationHistoryEntry[];
};

export type TiVoPublicationHistoryEntry = {
  clientTypeId: string | null;
  oemId: string;
  releaseStages: Record<ReleaseStage, TiVoPublicationHistoryState>;
};

export type TiVoPublicationHistoryState =
  | "published"
  | "promotedNotTargeted"
  | "promotedTargetedNotPublished"
  | "notPromoted"
  | "notPromotableOemRestricted";

export type TiVoSnapshotStatus = {
  id: string;
  active: boolean;
  created_at: string;
};
